// Tanner Fry
// tannerf1101@yahoo.com
// API calls for users.

import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { getCorrectDomain } from "../AppSettings";
import { RootState } from "../AppStore";
import { SubscriptionPlan } from "../assets/components/data/SubscriptionData";
import { IResponseLogin, IResponseSubscriber } from "../interfaces/api_response_data";
import { HTTP_STATUS } from "../interfaces/api_responses_types";
import { setUser } from "../slices/UserSlice";
import { fetchAPIGET, fetchAPIPOSTByUserUuid } from "./Common";

export interface IUserFromUsersList {
    id: number,
    email: string,
    firstName: string,
    lastName: string,
}

export interface ReturnPropsAPIGetUsers {
    users: IUserFromUsersList[],
    isAPIDown: boolean
}

export interface IUserPartial {
    data: {
        uuid: string,
        token: string,
        firstName: string,
        lastName: string,
        email: string,
        isActive: boolean,
        hasOtpSetup: boolean,
        latestOtp: string
    },
    error: string
}

export interface IUserPartialClean {
    data: {
        uuid: string,
        token: string,
        firstName: string,
        lastName: string,
        email: string,
        isActive: boolean,
        hasOtpSetup: boolean,
        latestOtp: string
    },
    error: string
}

export const APIGetUsers = () => {
    return fetch(getCorrectDomain() + '/api/users', {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            // Token from local storage
            'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('user')).token,
        },
    }).then(function (response) {
        if (response.ok) {
            return response.json();
        }
        return Promise.reject(response);
    }).then(function (data) {
        console.log("Fetched user list data: " + JSON.stringify(data[0]));
        const userData = [];
        userData.push(data[0]);
        const returnData: ReturnPropsAPIGetUsers = {
            users: data[0].map((user) => { return user; }),
            isAPIDown: false
        }
        return returnData;
    }).catch(function (error) {
        console.log("API user list fetch error: '" + JSON.stringify(error) + "'.");
        console.log("Is return equal to 'TypeError: Failed to fetch': " + (error === "TypeError: Failed to fetch"));
        if (error === "TypeError: Failed to fetch") {
            const returnData: ReturnPropsAPIGetUsers = {
                users: [],
                isAPIDown: true
            }
            return returnData;
        }
    });
}

export interface ReturnPropsAPIGetUserLoginResponse {
    newNavigationRoute: string,
    isSubscriber: boolean,
    subscriberPlan: SubscriptionPlan,
    hasOTPSetup: boolean,
    error: string
}

export const APIGetUserLoginResponse = (userEmail: string, userPassword: string, dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    let returnLoginData: ReturnPropsAPIGetUserLoginResponse = {
        newNavigationRoute: '',
        isSubscriber: false,
        subscriberPlan: SubscriptionPlan.Free,
        hasOTPSetup: false,
        error: ''
    }

    return fetch(getCorrectDomain() + '/api/users/login', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            email: userEmail,
            passHash: userPassword
        })
    }).then(function (response: Response) {
        if (response.ok) {
            return response;
        }
        return Promise.reject(response);
    }).then(async function (response: Response) {
        // TODO: Login user and store info into cookies
        console.log("APIGetUserLoginResponse response: ", response);
        const responseLoginData: IResponseLogin = await response.json();
        
        
        if (response.status === HTTP_STATUS.OK) {
            let userData = responseLoginData.data;
            console.log("User data from server: ", JSON.stringify(userData));
            
            // Store user info locally
            dispatch(setUser(userData));
            localStorage.setItem('user', JSON.stringify(userData));

            returnLoginData = {
                newNavigationRoute: '/dashboard',
                isSubscriber: false,
                subscriberPlan: SubscriptionPlan.Free,
                hasOTPSetup: userData.hasOtpSetup,
                error: ''
            }

            console.log("User return data: ", returnLoginData);

            // START PROCESS OF ASSIGNING DATA
            
            // TODO: Check if user has paid for a plan on Stripe. If so, set user's plan to paid
            // TODO: Make a request to our server which will then use the Stripe API to check if the user has a plan,
            //       essentially, via the steps below 
            // https://stripe.com/docs/api/charges/object?lang=node
            returnLoginData.isSubscriber = await fetch(getCorrectDomain() + '/api/users/is-subscriber', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: userEmail,
                })
            }).then(function (response: Response) {
                if (response.ok) {                    
                    return response;
                }
                return Promise.reject(response);
            }).then(async function (response: Response) {        
                if (response.status === HTTP_STATUS.OK) {
                    const responseSubscriberData: IResponseSubscriber = await response.json();
                    returnLoginData.isSubscriber = responseSubscriberData.data.isSubscriber;
                    returnLoginData.subscriberPlan = responseSubscriberData.data.plan;
                    return true;
                } else {
                    // TODO: Display message to user, error logging in.
                    console.log("Subscriber Result: ", JSON.stringify(response));
                    // Return new navigation direction: none since we want to stay on the page
                    return false;
                }
            }).catch(function (error) {
                console.warn('Something went wrong with api subscriber check.', error);
                return false;
            });

            // Return data
            return returnLoginData;
        } else {
            // TODO: Display message to user, error logging in.
            console.log("Error Results: ", JSON.stringify(response));
            returnLoginData = {
                newNavigationRoute: '',
                isSubscriber: false,
                subscriberPlan: SubscriptionPlan.Free,
                hasOTPSetup: false,
                error: response[0].message
            }
            // Return new navigation direction: none since we want to stay on the page
            return returnLoginData;
        }
    }).catch(function (error) {
        console.warn('Something went wrong with api login request.', error);
        return returnLoginData;
    });
}

// api/verify/verify-email-otp
export interface ReturnPropsAPIPOSTOTPCode {
    data: VerifyOTPCodeData,
    error: string
}
export enum VerifyOTPCodeData {
    APPROVED = "approved",
    PENDING = "pending",
    CANCELED = "canceled",
    MAX_ATTEMPTS_REACHED = "max_attempts_reached",
    DELETED = "deleted",
    FAILED = "failed",
    EXPIRED = "expired",
    UNKNOWN = "unknown",
}
export const ApiPostOtpCode = async (otpCode) => {
    return fetchAPIPOSTByUserUuid<ReturnPropsAPIPOSTOTPCode>('/api/verify/verify-email-otp', {
        otp: otpCode
    }).then((response) => {
        console.log("APIPOSTOTPCode response: ", response);
        return response;
    }).catch((error) => {
        console.warn('Something went wrong.', error);
        return error;
    });
}

// api/users/update-otp-state
export const ApiPostUpdateUserOtpState = (toggleChecked: boolean) => {
    let toggleCheckedNumber: number = 0;
    if (toggleChecked) {
        toggleCheckedNumber = 1;
    } else {
        toggleCheckedNumber = 0;
    }
    console.log("Toggle checked number: ", toggleCheckedNumber);

    return fetchAPIPOSTByUserUuid<IUserPartial>('/api/users/update-otp-state', {
        hasOtpSetup: toggleCheckedNumber
    });
}

export const ApiGetUserData = () => {
    return fetchAPIGET<IUserPartial>('/api/users/user');
}